import { ref, type Ref } from 'vue'
import { EventNameEnum } from '@/types/generated'
import { OpenAPI } from '@/types/generated/core/OpenAPI'
import { useMetrics } from '@/composables/useMetrics'
import { VERDICT_SOURCE_PROVIDER } from '@/composables/useVerdictLabels'

interface UseVerdictPdfActionsOptions {
  chatType: Ref<string>
  messageUuid: Ref<string>
}

export function useVerdictPdfActions({ chatType, messageUuid }: UseVerdictPdfActionsOptions) {
  const { trackEvent } = useMetrics()
  const pdfErrorUuids = ref<string[]>([])

  const fetchVerdictPdfUrl = async (uuid: string): Promise<string> => {
    const response = await fetch(`${OpenAPI.BASE}/api/v3/court_judgements/${uuid}/pdf/`, {
      credentials: 'include'
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const data = await response.json()
    return data.url
  }

  const clearPdfError = (uuid: string) => {
    pdfErrorUuids.value = pdfErrorUuids.value.filter((id) => id !== uuid)
  }

  const baseEventProps = (uuid: string) => ({
    court_judgement_uuid: uuid,
    message_uuid: messageUuid.value,
    chat_type: chatType.value,
    source_provider: VERDICT_SOURCE_PROVIDER
  })

  const openVerdictPdf = async (uuid: string) => {
    clearPdfError(uuid)
    const verdictBaseProps = baseEventProps(uuid)
    try {
      const url = await fetchVerdictPdfUrl(uuid)
      window.open(url, '_blank', 'noopener')
      // Dual event on success — intentional and temporary:
      //  - VERDICT_PREVIEW_OPENED is the new canonical event (GRO-402) and the
      //    one product dashboards should migrate to.
      //  - VERDICT_PDF_OPENED is kept for backwards-compatibility with existing
      //    PostHog/Segment dashboards and alerts that still key off the legacy
      //    name. Payloads are identical; downstream consumers can deduplicate
      //    on (event, court_judgement_uuid, timestamp).
      // Remove VERDICT_PDF_OPENED once all consumers have been migrated.
      void trackEvent(EventNameEnum.VERDICT_PREVIEW_OPENED, verdictBaseProps)
      void trackEvent(EventNameEnum.VERDICT_PDF_OPENED, verdictBaseProps)
    } catch {
      // Signed URL fetch failed (network / HTTP / JSON). We classify this as a
      // preview failure so the UI can surface the inline error affordance.
      pdfErrorUuids.value.push(uuid)
      void trackEvent(EventNameEnum.VERDICT_PREVIEW_FAILED, verdictBaseProps)
    }
  }

  const downloadVerdictPdf = async (uuid: string, fileNumber: string | null) => {
    clearPdfError(uuid)
    try {
      const url = await fetchVerdictPdfUrl(uuid)
      const fileResponse = await fetch(url)
      if (!fileResponse.ok) throw new Error()
      const blob = await fileResponse.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = fileNumber ? `${fileNumber}.pdf` : `verdict_${uuid}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
      void trackEvent(EventNameEnum.VERDICT_DOWNLOADED, baseEventProps(uuid))
    } catch {
      pdfErrorUuids.value.push(uuid)
      void trackEvent(EventNameEnum.VERDICT_DOWNLOAD_FAILED, baseEventProps(uuid))
    }
  }

  return {
    pdfErrorUuids,
    openVerdictPdf,
    downloadVerdictPdf
  }
}
